/**
 * Security utilities for input sanitization and output encoding
 * Following OWASP guidelines for preventing injection attacks
 */

/**
 * Sanitize string input - removes HTML tags, scripts, and dangerous patterns
 * @param input - The string to sanitize
 * @param preserveSpaces - If true, preserves spaces (useful during typing). Default: false
 */
export function sanitizeString(input: string, preserveSpaces: boolean = false): string {
  if (typeof input !== "string") {
    return "";
  }

  let result = input
    // Remove HTML tags
    .replace(/<[^>]*>/g, "")
    // Remove script tags and event handlers
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, "")
    // Remove SQL injection patterns (basic) - but preserve apostrophes in names/addresses
    // Only remove if they're part of SQL patterns, not regular text
    .replace(/['"]/g, "")
    // Remove backslashes and semicolons (SQL injection)
    .replace(/[\\;]/g, "")
    // Remove control characters
    .replace(/[\x00-\x1F\x7F]/g, "");

  // Normalize whitespace (multiple spaces to single space)
  if (!preserveSpaces) {
    result = result.replace(/\s+/g, " ");
  }

  // Only trim if not preserving spaces (during typing)
  if (!preserveSpaces) {
    result = result.trim();
  }

  return result;
}

/**
 * Sanitize phone number to E.164 format
 */
export function sanitizePhone(phone: string, prefix: string = "+1"): string {
  if (!phone) return "";

  // Remove all non-digit characters except +
  let normalized = phone.trim();
  if (normalized.startsWith("+")) {
    normalized = "+" + normalized.slice(1).replace(/\D/g, "");
  } else {
    normalized = normalized.replace(/\D/g, "");
    // Add prefix if not present
    if (prefix && !normalized.startsWith(prefix.replace(/\D/g, ""))) {
      normalized = prefix.replace(/\D/g, "") + normalized;
    }
  }

  return normalized;
}

/**
 * Sanitize email address
 */
export function sanitizeEmail(email: string): string {
  if (!email) return "";

  return email
    .trim()
    .toLowerCase()
    // Remove potentially dangerous characters
    .replace(/[<>]/g, "")
    // Remove control characters
    .replace(/[\x00-\x1F\x7F]/g, "");
}

/**
 * Sanitize address input
 * @param address - The address to sanitize
 * @param preserveSpaces - If true, preserves spaces (useful during typing). Default: false
 */
export function sanitizeAddress(address: string, preserveSpaces: boolean = false): string {
  if (!address) return "";

  let result = address
    // Remove HTML tags
    .replace(/<[^>]*>/g, "")
    // Remove script patterns
    .replace(/javascript:/gi, "")
    .replace(/on\w+\s*=/gi, "")
    // Remove dangerous characters but preserve apostrophes in addresses
    .replace(/["]/g, "")
    // Remove backslashes and semicolons
    .replace(/[\\;]/g, "")
    // Remove control characters
    .replace(/[\x00-\x1F\x7F]/g, "")
    // Limit length
    .slice(0, 200);

  // Normalize whitespace (multiple spaces to single space)
  if (!preserveSpaces) {
    result = result.replace(/\s+/g, " ");
  }

  // Only trim if not preserving spaces (during typing)
  if (!preserveSpaces) {
    result = result.trim();
  }

  return result;
}

/**
 * Escape HTML entities to prevent XSS
 */
export function escapeHtml(text: string): string {
  if (typeof text !== "string") {
    return String(text);
  }

  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };

  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}

/**
 * Sanitize numeric input
 */
export function sanitizeNumber(input: string | number): number | null {
  if (typeof input === "number") {
    return isNaN(input) ? null : input;
  }

  const cleaned = String(input).replace(/[^\d.-]/g, "");
  const parsed = parseFloat(cleaned);

  return isNaN(parsed) ? null : parsed;
}

/**
 * Sanitize URL input (basic validation)
 */
export function sanitizeUrl(url: string): string | null {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Remove SQL injection patterns (basic protection)
 */
export function removeSqlInjectionPatterns(input: string): string {
  if (typeof input !== "string") {
    return "";
  }

  return input
    // Remove SQL comment patterns
    .replace(/--/g, "")
    .replace(/\/\*/g, "")
    .replace(/\*\//g, "")
    // Remove UNION patterns
    .replace(/union\s+select/gi, "")
    // Remove DROP patterns
    .replace(/drop\s+table/gi, "")
    // Remove DELETE patterns
    .replace(/delete\s+from/gi, "")
    // Remove INSERT patterns
    .replace(/insert\s+into/gi, "")
    // Remove UPDATE patterns
    .replace(/update\s+.*\s+set/gi, "");
}

/**
 * Sanitize object recursively
 */
export function sanitizeObject<T extends Record<string, any>>(obj: T): T {
  const sanitized = { ...obj };

  for (const key in sanitized) {
    if (typeof sanitized[key] === "string") {
      sanitized[key] = sanitizeString(sanitized[key]);
    } else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
      sanitized[key] = sanitizeObject(sanitized[key]);
    }
  }

  return sanitized;
}

